import {AcquisitionListStatusEnum} from '@/enums/institutioner/acquisition/AcquisitionListStatus.enum';
import {AcquisitionProgressStageEnum} from '@/enums/institutioner/acquisition/AcquisitionProgressStage.enum';

/** Display order, bar colors and soft tag tones of the acquisition status in the redesigned views */
export const STATUS_ORDER: AcquisitionListStatusEnum[] = [
    AcquisitionListStatusEnum.OPEN,
    AcquisitionListStatusEnum.POTENTIAL,
    AcquisitionListStatusEnum.AI_SUCCESS,
    AcquisitionListStatusEnum.IN_PROGRESS,
    AcquisitionListStatusEnum.COMMITTED,
    AcquisitionListStatusEnum.CLOSED,
];

const BAR: Record<AcquisitionListStatusEnum, string> = {
    [AcquisitionListStatusEnum.OPEN]: '#B8C0CA',
    [AcquisitionListStatusEnum.POTENTIAL]: '#64A8EE',
    [AcquisitionListStatusEnum.AI_SUCCESS]: '#26A69A',
    [AcquisitionListStatusEnum.IN_PROGRESS]: '#1976D2',
    [AcquisitionListStatusEnum.COMMITTED]: '#2E7D32',
    [AcquisitionListStatusEnum.CLOSED]: '#E5484D',
};

const TONE: Record<AcquisitionListStatusEnum, string> = {
    [AcquisitionListStatusEnum.OPEN]: 'tone-muted',
    [AcquisitionListStatusEnum.POTENTIAL]: 'tone-info',
    [AcquisitionListStatusEnum.AI_SUCCESS]: 'tone-teal',
    [AcquisitionListStatusEnum.IN_PROGRESS]: 'tone-info',
    [AcquisitionListStatusEnum.COMMITTED]: 'tone-good',
    [AcquisitionListStatusEnum.CLOSED]: 'tone-bad',
};

export function statusBarColor(status?: AcquisitionListStatusEnum | null): string {
    return BAR[status ?? AcquisitionListStatusEnum.OPEN] || BAR[AcquisitionListStatusEnum.OPEN];
}

export function statusTone(status?: AcquisitionListStatusEnum | null): string {
    return TONE[status ?? AcquisitionListStatusEnum.OPEN] || 'tone-muted';
}

export function statusLabel(status?: AcquisitionListStatusEnum | null): string {
    return AcquisitionListStatusEnum.Record[status ?? AcquisitionListStatusEnum.OPEN]?.text || 'Offen';
}

/* Sub-states of "In Arbeit" (sales agency): bar colors and tones */
const STAGE_BAR: Record<AcquisitionProgressStageEnum, string> = {
    [AcquisitionProgressStageEnum.NOT_REACHED]: '#90A4AE',
    [AcquisitionProgressStageEnum.FOLLOW_UP]: '#1976D2',
    [AcquisitionProgressStageEnum.HOT_FOLLOW_UP]: '#F59E0B',
    [AcquisitionProgressStageEnum.SCHEDULED]: '#00897B',
};

const STAGE_TONE: Record<AcquisitionProgressStageEnum, string> = {
    [AcquisitionProgressStageEnum.NOT_REACHED]: 'tone-muted',
    [AcquisitionProgressStageEnum.FOLLOW_UP]: 'tone-info',
    [AcquisitionProgressStageEnum.HOT_FOLLOW_UP]: 'tone-warn',
    [AcquisitionProgressStageEnum.SCHEDULED]: 'tone-teal',
};

export function stageLabel(stage?: AcquisitionProgressStageEnum | null): string {
    return stage ? AcquisitionProgressStageEnum.Record[stage]?.text || stage : '';
}

export function stageIcon(stage?: AcquisitionProgressStageEnum | null): string {
    return stage ? AcquisitionProgressStageEnum.Record[stage]?.icon || '' : '';
}

export function stageBarColor(stage?: AcquisitionProgressStageEnum | null): string {
    return (stage && STAGE_BAR[stage]) || BAR[AcquisitionListStatusEnum.IN_PROGRESS];
}

export function stageTone(stage?: AcquisitionProgressStageEnum | null): string {
    return (stage && STAGE_TONE[stage]) || TONE[AcquisitionListStatusEnum.IN_PROGRESS];
}

/** The stage only belongs to "In Arbeit"; other statuses ignore it */
export function stageOf(status?: AcquisitionListStatusEnum | null, stage?: AcquisitionProgressStageEnum | null): AcquisitionProgressStageEnum | null {
    return status === AcquisitionListStatusEnum.IN_PROGRESS && stage ? stage : null;
}

/** "In Arbeit · Hot Follow-Up", or the plain status label */
export function statusLabelWithStage(status?: AcquisitionListStatusEnum | null, stage?: AcquisitionProgressStageEnum | null): string {
    const effective = stageOf(status, stage);
    return effective ? `${statusLabel(status)} · ${stageLabel(effective)}` : statusLabel(status);
}

/** Tone of the status, refined by the stage while "In Arbeit" */
export function statusToneWithStage(status?: AcquisitionListStatusEnum | null, stage?: AcquisitionProgressStageEnum | null): string {
    const effective = stageOf(status, stage);
    return effective ? stageTone(effective) : statusTone(status);
}

/** Bar color of the status, refined by the stage while "In Arbeit" */
export function statusBarColorWithStage(status?: AcquisitionListStatusEnum | null, stage?: AcquisitionProgressStageEnum | null): string {
    const effective = stageOf(status, stage);
    return effective ? stageBarColor(effective) : statusBarColor(status);
}

export type StatusCounts = Partial<Record<string, number>>;

/** Segment of a stacked status bar */
export type BarSegment = { key: string; count: number; color: string; text: string };

/** Counts per status, keys are the numeric enum values as strings */
export function countStatuses(statuses: Array<AcquisitionListStatusEnum | undefined | null>): StatusCounts {
    const counts: StatusCounts = {};
    for (const status of statuses) {
        const key = String(status ?? AcquisitionListStatusEnum.OPEN);
        counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
}

/** Status comments can be stored as HTML (rich text); shows them as plain text */
export function plainText(value?: string | null): string {
    if (!value) {
        return '';
    }
    const element = document.createElement('div');
    element.innerHTML = value.replace(/<\/(p|div|li)>/gi, ' ').replace(/<br\s*\/?>/gi, ' ');
    return (element.textContent || '').replace(/\s+/g, ' ').trim();
}
